import * as members from '../../privates/privateMembers';
import isAllowedComponentAPI from '../../privates/utils/componentAPISupport';
import reporter from '../../privates/reporter';
import {validate} from '../../privates/validations/validation';
import {setAsyncFunctions} from '../../privates/utils/asyncDecorator';
import {setStageUnfriendlyFunctions} from '../../privates/utils/livePreview';

/**
 * @mixin PlayableMixin
 * @summary Provides functionality for elements that can be played.
 * @memberof $w
 */
const autoPlayOverrides = {
    '$w.Slideshow': 'autoPlay'
};

function reportUnsupportedAPI(api, type) {
    reporter.reportSdkWarning(`'${api}' is not supported for an element of type: ${type}.`);
}

function executeApiIfSupported(comp, apiName, apiToInvoke) {
    const viewerType = members._type.get(comp).viewerType;
    if (isAllowedComponentAPI(viewerType, apiName)) {
        return apiToInvoke();
    }
    reportUnsupportedAPI(apiName, viewerType); //todo Shimi_Liderman 21/05/2017 18:53 Seems very strange to print out the viewer type!
}

function setAutoplay(comp, autoplay) {
    const compType = members._type.get(comp).compType;
    if (validate({
        compType,
        functionName: 'autoplay',
        dataToValidate: {autoplay},
        compName: comp.id
    })) {
        const RMI = members._RMI.get(comp);
        const compId = members._compId.get(comp);
        const obj = {};
        obj[autoPlayOverrides[comp.type] ? autoPlayOverrides[comp.type] : 'autoplay'] = autoplay;
        RMI.setProps(compId, obj);
    }
}

class PlayableMixin {

    // PROPERTIES:

    /**
     * @member isPlaying
     * @label property
     * @syntax
     * get isPlaying(): boolean
     * @summary Indicates if the element is currently playing.
     * @description
     *  To set the `isPlaying` property on an element, use the element's
     *  [`play()`](#play) and [`pause()`](#pause) functions.
     *
     *  If you select **Autoplays on loading** in the element's Settings panel
     *  in the Editor, the `isPlaying` property is set to `true` when the page loads.
     * @snippet [PlayableMixin-isPlaying.es6=Get an element's current playing status]
     * @snippet [PlayableMixin_toggle_isPlaying.es6=Toggle an element's current playing state]
     * @type {external:Boolean}
     * @see [play( )](#play)
     * @see [pause( )](#pause)
     * @memberof $w.PlayableMixin
     * @readonly
     */
    get isPlaying() {
        return executeApiIfSupported(this, 'isPlaying', () => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            return RMI.getState(compId).isPlaying;
        });
    }

    // METHODS:

    /**
     * @function onPause
     * @syntax
     * function onPause(handler: EventHandler): Gallery | Slideshow
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when playback is paused.
     * @description
     *  An element can be paused by a user clicking the gallery or slideshow's
     *  pause button or by calling its [`pause()`](#pause) function
     *
     * > **Notes:**
     * > + When using `onPause()` on a [gallery]($w.Gallery.html), make sure the
     *  [gallery]($w.Gallery.html) is one where the `isPlayable`
     *  [capability](#galleryCapabilities) is `true`.
     * >
     * >
     * > + If you [change a gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `onPause()`, your code may no longer function correctly.
     * >
     * >
     * > + **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * @snippet [PlayableMixin-onPause.es6=Get the ID of the element that has been paused]
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the element is paused.
     * @returns {$w.Gallery|$w.Slideshow} The gallery or slideshow that triggered the event.
     * @see [pause( )](#pause)
     * @memberof $w.PlayableMixin
     * @eventtype autoplayOff
     * @eventhandler
     */
    onPause(cb) {
        executeApiIfSupported(this, 'onPause', () => {
            const compType = members._type.get(this).compType;
            if (validate({
                compType,
                functionName: 'onPause',
                dataToValidate: {onPause: cb},
                compName: this.id
            })) {
                const RMI = members._RMI.get(this);
                const contextId = members._contextId.get(this);
                const controllerId = members._controllerId.get(this);
                const compId = members._compId.get(this);
                RMI.registerEvent(contextId, compId, RMI.getEventTypes().AUTOPLAY_OFF, controllerId, cb);
            }
        });
        return this;
    }

    /**
     * @function onPlay
     * @syntax
     * function onPlay(handler: EventHandler): Gallery | Slideshow
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when playback is started or restarted.
     * @description
     *  An element can be played by a user clicking the gallery or slideshow's
     *  play button or by calling its [`play()`](#play) function.
     * 
     * > **Notes:**
     * > + When using `onPlay()` on a [gallery]($w.Gallery.html), make sure the
     *  [gallery]($w.Gallery.html) is one where the `isPlayable`
     *  [capability](#galleryCapabilities) is `true`.
     * >
     * >
     * > + If you [change a gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `onPlay()`, your code may no longer function correctly.
     * >
     * >
     * > + **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * @snippet [PlayableMixin-onPlay.es6=Get the ID of the element that is playing]
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the element is played.
     * @returns {$w.Gallery|$w.Slideshow} The gallery or slideshow that triggered the event.
     * @see [play( )](#play)
     * @memberof $w.PlayableMixin
     * @eventtype autoplayOn
     * @eventhandler
     */
    onPlay(cb) {
        executeApiIfSupported(this, 'onPlay', () => {
            const compType = members._type.get(this).compType;
            if (validate({
                compType,
                functionName: 'onPlay',
                dataToValidate: {onPlay: cb},
                compName: this.id
            })) {
                const RMI = members._RMI.get(this);
                const contextId = members._contextId.get(this);
                const controllerId = members._controllerId.get(this);
                const compId = members._compId.get(this);
                RMI.registerEvent(contextId, compId, RMI.getEventTypes().AUTOPLAY_ON, controllerId, cb);
            }
        });
        return this;
    }

    /**
     * @function pause
     * @syntax
     * function pause(): void;
     * @summary Pauses playback.
     * @description
     *  The `pause()` function pauses a slideshow or playable gallery and fires a `pause` event.
     *
     *  The `pause` event handlers set on this element by the [onPause( )](#onPause)
     *  function or in the Editor are called.
     *
     * > **Notes:**
     * > + When using `pause()` on a [gallery]($w.Gallery.html), make sure the
     *  [gallery]($w.Gallery.html) is one where the `isPlayable`
     *  [capability](#galleryCapabilities) is `true`.
     * >
     * >
     * > + If you [change a gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `pause()`, your code may no longer function correctly.
     * @snippet [PlayableMixin-pause.es6=Pauses playback]
     * @snippet [PlayableMixin_toggle_isPlaying.es6=Toggle an element's current playing state]
     * @see [play( )](#play)
     * @see [onPause( )](#onPause)
     * @memberof $w.PlayableMixin
     */
    pause() {
        executeApiIfSupported(this, 'pause', () => {
            setAutoplay(this, false);
        });
        return this;
    }

    /**
     * @function play
     * @syntax
     * function play(): void;
     * @summary Begins or resumes playback.
     * @description
     *  The `play()` function plays a slideshow or playable gallery and fires a `play` event.
     *
     *  The `play` event handlers set on this element by the [onPlay( )](#onPlay)
     *  function or in the Editor are called.
     *
     * > **Notes:**
     * > + When using `play()` on a [gallery]($w.Gallery.html), make sure the
     *  [gallery]($w.Gallery.html) is one where the `isPlayable`
     *  [capability](#galleryCapabilities) is `true`.
     * >
     * >
     * > + If you [change a gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `play()`, your code may no longer function correctly.
     * @snippet [PlayableMixin-play.es6=Begins or resumes playback]
     * @snippet [PlayableMixin_toggle_isPlaying.es6=Toggle an element's current playing state]
     * @see [pause( )](#pause)
     * @see [onPlay( )](#onPlay)
     * @memberof $w.PlayableMixin
     */
    play() {
        executeApiIfSupported(this, 'play', () => {
            setAutoplay(this, true);
        });
        return this;
    }

    /**
     * @function next
     * @syntax
     * function next(): Promise<Element>
     * @summary Moves to the next item.
     * @description
     *  The `next()` function returns a Promise that is resolved when the next
     *  item is completely rendered. Calling `next()` when on the last item
     *  moves to the first item.
     *
     * > **Notes:**
     * > + When using `next()` on a [gallery]($w.Gallery.html), make sure the
     *  [gallery]($w.Gallery.html) is one where the `isPlayable`
     *  [capability](#galleryCapabilities) is `true`.
     * >
     * >
     * > + If you [change a gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `next()`, your code may no longer function correctly.
     * @snippet [PlayableMixin-next.es6=Move to the next item]
     * @snippet [PlayableMixin-next_then.es6=Move to the next item and log a message when done]
     * @returns {external:Promise}
     * @fulfill {$w.Element} The newly displayed image or slide.
     * @see [previous( )](#previous)
     * @see [play( )](#play)
     * @see [pause( )](#pause)
     * @memberof $w.PlayableMixin
     */
    next() {
        const viewerType = members._type.get(this).viewerType;
        return executeApiIfSupported(this, 'next', () => {
            return new Promise((resolve) => {
                const RMI = members._RMI.get(this);
                const compId = members._compId.get(this);
                RMI.executeCompBehavior(compId, 'nextSlide', {}, () => {
                    resolve();
                });
            });
        }) || Promise.reject(`Next is not supported for an element of type: ${viewerType}.`);
    }

    /**
     * @function previous
     * @syntax
     * function previous(): Promise<Element>
     * @summary Moves to the previous image or slide.
     * @description
     *  The `previous()` function returns a Promise that is resolved when the previous
     *  image or slide is completely rendered. Calling `previous()` when on the first item
     *  moves to the last item.
     *
     * > **Notes:**
     * > + When using `previous()` on a [gallery]($w.Gallery.html), make sure the
     *  [gallery]($w.Gallery.html) is one where the `isPlayable`
     *  [capability](#galleryCapabilities) is `true`.
     * >
     * >
     * > + If you [change a gallery's type](https://www.wix.com/support/html5/article/changing-the-gallery-type)
     *  to one that doesn't support `previous()`, your code may no longer function correctly.
     * @snippet [PlayableMixin-previous.es6=Move to the previous item]
     * @snippet [PlayableMixin-previous_then.es6=Move to the previous item and log a message when done]
     * @returns {external:Promise}
     * @fulfill {$w.Element} The newly displayed image or slide.
     * @see [next( )](#next)
     * @memberof $w.PlayableMixin
     */
    previous() {
        const viewerType = members._type.get(this).viewerType;
        return executeApiIfSupported(this, 'previous', () => {
            return new Promise((resolve) => {
                const RMI = members._RMI.get(this);
                const compId = members._compId.get(this);
                RMI.executeCompBehavior(compId, 'prevSlide', {}, () => {
                    resolve();
                });
            });
        }) || Promise.reject(`Previous is not supported for an element of type: ${viewerType}.`);
    }
}

setAsyncFunctions(['next', 'previous']);
setStageUnfriendlyFunctions(PlayableMixin, 'play');

export default PlayableMixin;
